import { useState, useEffect, useCallback } from "react";
import {
  X, MagnifyingGlass, Spinner, UserPlus, LinkSimple, Buildings, Phone,
  Warning, CheckCircle,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import {
  searchPersonsForLink, linkOrphanToPerson,
} from "@/lib/api";

/**
 * LinkOrphanDialog
 * ────────────────────────────────────────────────────────────────
 * Lets a rep attach an orphan inbound phone number to a person record
 * directly from inside the chat view. Two tabs:
 *   • "Existing person" — search → pick → choose slot (main/alt) → link
 *   • "New person"      — pick a customer (via the same search) → enter
 *                         the contact's name → create + link
 *
 * On success the parent receives the new {customer_id, pfcid, name,
 * company_name, resolved_count} so it can switch the chat to known mode
 * and refresh the conversation list.
 */
export default function LinkOrphanDialog({ phone, onClose, onLinked }) {
  const [tab, setTab] = useState("existing"); // "existing" | "new"
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [slot, setSlot] = useState("alt");
  const [force, setForce] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Debounced search.
  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]); setSearching(false); return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await searchPersonsForLink(q.trim(), 20);
        if (!cancelled) setResults(r.items || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || e?.message || "Search failed");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const onPick = useCallback((p) => { setPicked(p); setError(null); setForce(false); }, []);

  const submitLink = async () => {
    if (!picked || !phone) return;
    setSubmitting(true); setError(null);
    try {
      const res = await linkOrphanToPerson({
        phone,
        action: tab === "existing" ? "link_existing" : "create_new",
        customer_id: picked.customer_id,
        pfcid: tab === "existing" ? picked.pfcid : undefined,
        slot: tab === "existing" ? slot : undefined,
        force: tab === "existing" ? force : undefined,
        name: tab === "new" ? newName : undefined,
        email: tab === "new" ? newEmail : undefined,
      });
      onLinked?.(res);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // FastAPI returns 409 with structured detail when the slot is occupied.
      if (e?.response?.status === 409 && typeof detail === "object" && detail.code === "slot_occupied") {
        setError({
          kind: "slot_occupied",
          message: detail.message,
          current: detail.current,
        });
      } else {
        setError({ kind: "generic", message: detail || e?.message || "Link failed" });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const slotConflict = picked && tab === "existing" && (
    (slot === "main" && picked.phone && picked.phone !== phone) ||
    (slot === "alt" && picked.phone_alt && picked.phone_alt !== phone)
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4"
      style={{ background: "rgba(15, 23, 42, 0.6)" }}
      onClick={onClose}
      data-testid="link-orphan-dialog"
    >
      <div
        className="bg-white rounded-md shadow-2xl w-full max-w-xl flex flex-col max-h-[80vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-3 flex items-center justify-between border-b border-slate-200">
          <div>
            <h3 className="text-base font-bold tracking-tight font-['Chivo']">Link orphan number</h3>
            <p className="text-xs text-slate-500 font-mono mt-0.5">{phone}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="link-orphan-close"
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500"
          ><X size={18} weight="bold" /></button>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3">
          <div className="inline-flex items-center rounded-full overflow-hidden text-[11px] font-bold uppercase tracking-wider bg-slate-100 border border-slate-200">
            <button
              onClick={() => { setTab("existing"); setPicked(null); setError(null); }}
              data-testid="link-orphan-tab-existing"
              className={`px-3 h-7 inline-flex items-center gap-1 ${tab === "existing" ? "bg-emerald-600 text-white" : "text-slate-500"}`}
            >
              <LinkSimple size={11} weight="bold" /> Existing person
            </button>
            <button
              onClick={() => { setTab("new"); setPicked(null); setError(null); }}
              data-testid="link-orphan-tab-new"
              className={`px-3 h-7 inline-flex items-center gap-1 ${tab === "new" ? "bg-emerald-600 text-white" : "text-slate-500"}`}
            >
              <UserPlus size={11} weight="bold" /> New person
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {/* Search box (used for both tabs) */}
          <div className="relative mb-3">
            <MagnifyingGlass size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              autoFocus
              data-testid="link-orphan-search"
              placeholder={tab === "existing" ? "Search by name, phone, email, or company…" : "Search the customer this person works at…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>

          {/* Search results */}
          {q.trim().length < 2 ? (
            <div className="text-xs text-slate-400 italic px-1 py-3">Type at least 2 characters to search.</div>
          ) : searching ? (
            <div className="flex items-center justify-center py-6">
              <Spinner size={18} className="animate-spin text-slate-400" />
            </div>
          ) : results.length === 0 ? (
            <div className="text-xs text-slate-500 px-1 py-3">No matches.</div>
          ) : (
            <div className="space-y-1 mb-3">
              {results.map((p) => {
                const isPicked = picked?.pfcid === p.pfcid;
                return (
                  <button
                    key={p.pfcid}
                    onClick={() => onPick(p)}
                    data-testid={`link-orphan-result-${p.pfcid}`}
                    className={`w-full text-left px-3 py-2 rounded-sm border transition-colors ${
                      isPicked ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <span className="truncate">{p.name || "(no name)"}</span>
                      {p.company_name && (
                        <span className="text-slate-400 text-xs inline-flex items-center gap-1 truncate">
                          <Buildings size={10} weight="fill" /> {p.company_name}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap gap-3">
                      {p.phone && (
                        <span className="inline-flex items-center gap-1 font-mono">
                          <Phone size={9} weight="fill" /> Main · {p.phone}
                        </span>
                      )}
                      {p.phone_alt && (
                        <span className="inline-flex items-center gap-1 font-mono">
                          <Phone size={9} /> Alt · {p.phone_alt}
                        </span>
                      )}
                      {p.email && <span className="truncate">{p.email}</span>}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Selection details (different per tab) */}
          {picked && tab === "existing" && (
            <div className="border border-emerald-200 bg-emerald-50/50 rounded-sm p-3 space-y-3" data-testid="link-orphan-existing-details">
              <div className="text-xs text-slate-700">
                Set <span className="font-mono font-bold">{phone}</span> as <span className="font-bold">{picked.name}</span>'s:
              </div>
              <div className="flex gap-2">
                {[
                  ["main", "Main", picked.phone],
                  ["alt", "Alt", picked.phone_alt],
                ].map(([val, label, occupied]) => (
                  <label
                    key={val}
                    className={`flex-1 text-xs px-3 py-2 rounded-sm border cursor-pointer transition-colors ${
                      slot === val ? "border-emerald-500 bg-white" : "border-slate-200 hover:bg-white"
                    }`}
                  >
                    <input type="radio" name="slot" value={val} checked={slot === val}
                           onChange={(e) => { setSlot(e.target.value); setForce(false); setError(null); }}
                           className="mr-2"
                           data-testid={`link-orphan-slot-${val}`} />
                    <span className="font-bold">{label}</span>
                    <div className="text-[10px] text-slate-500 mt-0.5 font-mono">
                      {occupied ? `→ ${occupied}` : "(empty)"}
                    </div>
                  </label>
                ))}
              </div>
              {slotConflict && (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-2 text-xs text-amber-900">
                  <div className="flex items-start gap-2">
                    <Warning size={12} weight="fill" className="text-amber-700 shrink-0 mt-0.5" />
                    <div>
                      The {slot} slot already holds <span className="font-mono font-bold">{slot === "main" ? picked.phone : picked.phone_alt}</span>.
                      Linking will <strong>overwrite</strong> it.
                    </div>
                  </div>
                  <label className="flex items-center gap-2 mt-2 text-xs">
                    <input type="checkbox" checked={force}
                           data-testid="link-orphan-force"
                           onChange={(e) => setForce(e.target.checked)} />
                    Yes, overwrite the existing {slot} number.
                  </label>
                </div>
              )}
            </div>
          )}

          {picked && tab === "new" && (
            <div className="border border-emerald-200 bg-emerald-50/50 rounded-sm p-3 space-y-3" data-testid="link-orphan-new-details">
              <div className="text-xs text-slate-700">
                Create a new contact under <span className="font-bold">{picked.company_name}</span>
                {" "}with main number <span className="font-mono font-bold">{phone}</span>:
              </div>
              <div>
                <label className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Contact name *</label>
                <Input
                  autoFocus
                  data-testid="link-orphan-new-name"
                  placeholder="e.g. Jane Doe"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="mt-1 h-9 text-sm"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase font-bold tracking-wider text-slate-500">Email (optional)</label>
                <Input
                  data-testid="link-orphan-new-email"
                  type="email"
                  placeholder="jane@acmecorp.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="mt-1 h-9 text-sm"
                />
              </div>
            </div>
          )}

          {error && (
            <div data-testid="link-orphan-error"
                 className="mt-3 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded-sm p-2 flex items-start gap-2">
              <Warning size={12} weight="fill" className="shrink-0 mt-0.5" />
              <span>{error.message || String(error)}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-between bg-slate-50">
          <div className="text-[10px] text-slate-500 max-w-xs">
            Linking will also re-stamp every historical message from {phone} so the
            full thread shows under the contact.
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              data-testid="link-orphan-cancel"
              className="h-8 px-4 text-xs font-bold uppercase tracking-wider text-slate-600 hover:bg-slate-200 rounded-sm"
            >Cancel</button>
            <button
              onClick={submitLink}
              disabled={
                submitting || !picked
                || (tab === "existing" && slotConflict && !force)
                || (tab === "new" && !newName.trim())
              }
              data-testid="link-orphan-submit"
              className="h-8 px-4 min-w-[120px] whitespace-nowrap text-xs font-bold uppercase tracking-wider text-white rounded-sm disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
              style={{ background: "#00a884" }}
            >
              {submitting ? <Spinner size={11} className="animate-spin" />
                          : <CheckCircle size={11} weight="bold" />}
              {tab === "existing" ? "Link" : "Create & Link"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
